import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';

import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { InstituteClass } from '@/types/instituteClass';

export interface HouseStudentFilterValues {
  search: string;
  classUuid: string;
  sectionUuid: string;
}

export const EMPTY_HOUSE_STUDENT_FILTERS: HouseStudentFilterValues = {
  search: '',
  classUuid: '',
  sectionUuid: '',
};

export interface HouseStudentFiltersProps {
  values: HouseStudentFilterValues;
  onChange: (values: HouseStudentFilterValues) => void;
  classes: InstituteClass[];
  /** A college has no class or section to narrow by. */
  hasPlacement: boolean;
  /** A house dropdown or control placed before the class and section selectors. */
  beforeClasses?: React.ReactNode;
  /** Anything else the caller narrows by, drawn at the end of the row. */
  extra?: React.ReactNode;
}

/**
 * Search, class and section — one row shared by a house's member list and the
 * Assign students picker, so the two narrow the same way.
 */
export function HouseStudentFilters({
  values,
  onChange,
  classes,
  hasPlacement,
  beforeClasses,
  extra,
}: HouseStudentFiltersProps) {
  const [draft, setDraft] = useState(values.search);
  const settled = useDebouncedValue(draft.trim(), 300);

  // Runs as somebody types, once they pause — the box has no button.
  useEffect(() => {
    if (settled !== values.search) onChange({ ...values, search: settled });
    // Only a settled draft should search; `values` catching up is not one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  // A reset from outside (another house chosen) clears the box too.
  useEffect(() => {
    if (values.search === '') setDraft('');
  }, [values.search]);

  const selectedClass = classes.find((one) => one.uuid === values.classUuid);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[200px]">
        <Input
          label="Search"
          type="search"
          autoComplete="off"
          placeholder="Name or admission number"
          leftIcon={<Search className="h-4 w-4" />}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </div>
      {beforeClasses}
      {hasPlacement && (
        <>
          <div className="w-full sm:w-36 lg:w-40 shrink-0">
            <Select
              label="Class"
              options={[
                { value: '', label: 'All classes' },
                ...classes.map((one) => ({ value: one.uuid, label: one.name })),
              ]}
              value={values.classUuid}
              // A section belongs to its class, so changing class clears it.
              onChange={(event) =>
                onChange({ ...values, classUuid: event.target.value, sectionUuid: '' })
              }
            />
          </div>
          <div className="w-full sm:w-32 lg:w-36 shrink-0">
            <Select
              label="Section"
              options={[
                { value: '', label: 'All sections' },
                ...(selectedClass?.sections ?? []).map((section) => ({
                  value: section.uuid,
                  label: section.name,
                })),
              ]}
              disabled={!selectedClass}
              value={values.sectionUuid}
              onChange={(event) => onChange({ ...values, sectionUuid: event.target.value })}
            />
          </div>
        </>
      )}
      {extra && <div className="shrink-0">{extra}</div>}
    </div>
  );
}
